import Moment from 'moment';

import SegmentUIModel from './segment';

import { getFormatDateTime } from '../../scripts/commons/commons';
import { PREMIER } from '../../scripts/constants/premier-types';

class LegUIModel {
  constructor({
    checkinStatus = undefined,
    estimatedDepartureTime = undefined,
    flightStatus = undefined,
    itineraryPartType = undefined,
    legRegion = undefined,
    manageStatus = undefined,
    marketFlight = undefined,
    regionKey = undefined,
    scheduledDepartureTime = undefined,
    seatSelectionType = undefined,
    segmentLegsOrigin = undefined,
    segments = undefined,
    standByListPaxLimit = undefined,
    standByReservation = undefined,
  } = {}) {
    this.checkinStatus = checkinStatus;
    this.estimatedDepartureTime = estimatedDepartureTime;
    this.flightStatus = flightStatus;
    this.itineraryPartType = itineraryPartType;
    this.legRegion = legRegion;
    this.manageStatus = manageStatus;
    this.marketFlight = marketFlight;
    this.regionKey = regionKey;
    this.scheduledDepartureTime = scheduledDepartureTime;
    this.seatSelectionType = seatSelectionType;

    let hasScheduleChange = false;

    if (segments !== undefined) {
      this.segmentsCollection = segments._collection.map(segment => {
        if (segment.segment) {
          segment.segment.marketFlightFromLeg = marketFlight;
          segment.segment.regionKeyFromLeg = regionKey;
        }

        if (segment.segment.segmentStatus === 'SC') {
          hasScheduleChange = true;
        }

        return new SegmentUIModel(segment);
      });

      this.legCode = hasScheduleChange && segmentLegsOrigin ? segmentLegsOrigin : segments.legCode;
      this.legType = segments.legType;
      this.totalFlightTimeInMinutes = segments.totalFlightTimeInMinutes;
      this.hasScheduleChange = hasScheduleChange;
    }

    this.standByListPaxLimit = standByListPaxLimit;
    this.standByReservation = standByReservation;
    this.totalLayoverDurationInMinutes = this._computeTotalLayoverDuration();
  }

  canOfferCBX() {
    return this.segmentsCollection.find(segmentModel => {
      return segmentModel.arrivalAirport === 'TIJ' || segmentModel.departureAirport === 'TIJ';
    });
  }

  /**
  * @Return {String} Arrival first segment
  */
  getArrivalDate(format) {
    const objDateTime = this._getFormatsDateTime('A');
    const date = getFormatDateTime(objDateTime, format);
    return date;
  }

  /**
  * @Return {String} Arrival first segment
  */
  getDepartureDate(format, language = '') {
    if (format !== undefined) {
      const objDateTime = this._getFormatsDateTime('D', language);
      return getFormatDateTime(objDateTime, format);
    }

    return this.segmentsCollection[0].departureDateTime;
  }

  /**
  * @Return {String} Destination last segment
  */
  getDestinationCode() {
    const sizeSegments = this.segmentsCollection.length;
    return this.segmentsCollection[sizeSegments - 1].arrivalAirport;
  }

  getFareFamily() {
    return this.segmentsCollection[0].fareFamily;
  }

  getBookingClass() {
    return this.segmentsCollection[0].bookingClass;
  }

  /**
  * @Return {String} Arrival first segment
  */
  getOriginCode() {
    return this.segmentsCollection[0].departureAirport;
  }

  hasTJXSegment() {
    return this.segmentsCollection.some(segmentModel => {
      return segmentModel.arrivalAirport === 'TJX' || segmentModel.departureAirport === 'TJX';
    });
  }

  isPremierLightBySegmentCode(segmentCode) {
    const segment = this.segmentsCollection.find(segmentModel => segmentModel.segmentCode === segmentCode);
    return segment && segment.isPremierLight;
  }

  getPremierTypeBySegmentCode(segmentCode) {
    const segment = this.segmentsCollection.find(segmentModel => segmentModel.segmentCode === segmentCode);

    if (segment) {
      if (segment.isPremierOne) {
        return PREMIER.ONE;
      } else if (segment.isPremierLight) {
        return PREMIER.LIGHT;
      } else {
        return PREMIER.PREMIER;
      }
    } else {
      return PREMIER.NO_OFFER;
    }
  }

  getRoutes() {
    const routes = this.segmentsCollection.map(segmentModel => {
      return segmentModel.getRoutes();
    });
    return routes;
  }

  getRoutesAvailablesForSeatMap() {
    const segmentsCollection = this.segmentsCollection.filter(segment =>
      !(segment.arrivalAirport === 'TJX' || segment.departureAirport === 'TJX')
    );
    const routes = segmentsCollection.map(segmentModel => {
      return segmentModel.getRoutes();
    });
    return routes;
  }

  getSegmentByCode(segmentCode) {
    return this.segmentsCollection.find(segmentModel => {
      return segmentCode === segmentModel.segmentCode;
    });
  }

  getSegmentByAirport(segment) {
    let segmentCode = null;
    this.segmentsCollection.forEach(segmentModel => {
      if (segmentModel.segmentCode.includes(segment.substr(0, segment.length - 4))) {
        segmentCode = segmentModel.segmentCode;
      }
    });

    return segmentCode;
  }

  /**
   * @return {Array} segment codes for leg
   */
  getSegmentCodes() {
    return this.segmentsCollection.map(segmentModel => segmentModel.segmentCode);
  }

  isBasicFareFamily() {
    return this.segmentsCollection[0].isBasicFareFamily();
  }

  isCodeShare() {
    return this.segmentsCollection.some(segment => {
      return segment.getOperatingCarrier() !== 'AM';
    });
  }

  /**
   * @return {Number} - Total layover duration in minutes
   */
  _computeTotalLayoverDuration() {
    let layovers = 0;

    if (this.segmentsCollection !== undefined) {
      this.segmentsCollection.forEach(segment => {
        layovers += segment.layoverToNextSegmentsInMinutes;
      });
    }

    return Moment.duration(layovers, 'minutes');
  }

  /**
  * @Return {Obj} All date formats
  * @Params {type} D = departure, A = arrival
  */
  _getFormatsDateTime(type, language = '') {
    const index = type === 'D' ? 0 : this.segmentsCollection.length - 1;
    const attr  = type === 'D' ? 'departureDateTime' : 'arrivalDateTime';
    const a     = this.segmentsCollection[index][attr].format('a');
    const D     = this.segmentsCollection[index][attr].format('D');
    let ddd   = this.segmentsCollection[index][attr].format('ddd');
    const DD    = this.segmentsCollection[index][attr].format('DD');
    let Do    = this.segmentsCollection[index][attr].format('Do');
    const HH    = this.segmentsCollection[index][attr].format('HH');
    const h     = this.segmentsCollection[index][attr].format('h');
    let MMM   = this.segmentsCollection[index][attr].format('MMM');
    let MMMM  = this.segmentsCollection[index][attr].format('MMMM');
    const mm    = this.segmentsCollection[index][attr].format('mm');
    const ss    = this.segmentsCollection[index][attr].format('ss');
    const YYYY  = this.segmentsCollection[index][attr].format('YYYY');
    const Z     = this.segmentsCollection[index][attr].format('Z');
    if (language !== '') {
      ddd   = this.segmentsCollection[index][attr].locale(language).format('ddd');
      Do    = this.segmentsCollection[index][attr].locale(language).format('Do');
      MMM   = this.segmentsCollection[index][attr].locale(language).format('MMM');
      MMMM  = this.segmentsCollection[index][attr].locale(language).format('MMMM');
    }
    return {
      'YYYY': YYYY,
      'MMMM': MMMM,
      'MMM': MMM,
      'ddd': ddd,
      'DD': DD,
      'Do': Do,
      'HH': HH,
      'mm': mm,
      'ss': ss,
      'a': a,
      'D': D,
      'h': h,
      'Z': Z,
    };
  }
}

export default LegUIModel;
